import React, { useState } from 'react'
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { Board } from '../campaign/components/board/board'
import logoLarge from '../assets/logo-large.png'
import { Button, Flex, Layout, Tooltip } from 'antd'
import { PageHeader } from '../components/page-header'
import { CandidateTimestampToggle } from '../components/candidate-timestamp-toggle'
import { CandidateAttributesToggle } from '../components/candidate-attributes-toggle'
import { CandidatePhoneToggle } from '../components/candidate-phone-toggle'
import { useAuth } from '../auth'
import { useTranslation } from 'react-i18next'
import { ContainerOutlined, ShrinkOutlined } from '@ant-design/icons'
import { useLocalStorage } from '../hooks/use-local-storage'
import { CandidateArchiveButton } from '../candidate/components/candidate-archive-button'
import { CandidateArchiveDrawer } from '../candidate/components/candidate-archive-drawer'

export const Route = createFileRoute('/_auth/board')({
    validateSearch: z.object({
        candidateId: z.string().optional(),
        archive: z.boolean().optional(),
    }),
    beforeLoad: ({ context }) => {
        // Company board not available for admins
        if (context.auth.isAdmin) {
            throw redirect({
                to: '/dashboard',
            })
        }
    },
    component: RouteComponent,
})

export default function RouteComponent() {
    const { candidateId, archive } = Route.useSearch()

    const [selected, setSelected] = useState<Record<string, boolean>>({})
    const navigate = useNavigate()

    const [, setCandidatesFullscreen] = useLocalStorage({
        key: 'candidates-fullscreen',
        defaultValue: false,
    })
    const { activeCompany } = useAuth()

    const { t } = useTranslation()

    return (
        <Layout
            className="p-4"
            style={{ height: '100vh' }}
        >
            <Flex
                vertical
                className="h-full"
                gap="large"
            >
                <Flex
                    gap="large"
                    className="lg:flex-row flex-col items-start lg:items-center"
                >
                    <Link to="/dashboard">
                        <img
                            alt="log"
                            width="169"
                            src={logoLarge}
                        />
                    </Link>
                    <PageHeader
                        title={t('Candidates')}
                        suffix={
                            <>
                                <CandidateTimestampToggle />
                                <CandidateAttributesToggle />
                                <CandidatePhoneToggle />
                                <Tooltip title={t('Close fullscreen')}>
                                    <Link
                                        to="/candidates"
                                        search={{}}
                                        onClick={() => setCandidatesFullscreen(false)}
                                    >
                                        <Button
                                            type="text"
                                            icon={<ShrinkOutlined />}
                                        />
                                    </Link>
                                </Tooltip>
                                <Tooltip title={t('Archive')}>
                                    <Link
                                        to="."
                                        search={{
                                            archive: true,
                                        }}
                                    >
                                        <Button
                                            type="text"
                                            icon={<ContainerOutlined />}
                                        />
                                    </Link>
                                </Tooltip>
                                {Object.keys(selected).length > 0 && (
                                    <CandidateArchiveButton
                                        onSuccess={() => setSelected({})}
                                        candidateIds={Object.keys(selected)}
                                    />
                                )}
                            </>
                        }
                    />
                </Flex>

                <Flex
                    gap="middle"
                    vertical
                    className="h-full"
                >
                    <Board
                        style={{
                            height: '100%',
                            maxWidth: '100%',
                        }}
                        height={1000}
                        columns={activeCompany?.columns || []}
                        candidateId={candidateId}
                        selected={selected}
                        setSelected={setSelected}
                    />
                </Flex>
                <CandidateArchiveDrawer
                    open={archive}
                    onClose={() => {
                        navigate({
                            to: '.',
                            search: (prev) => ({
                                ...prev,
                                archive: undefined,
                            }),
                        })
                    }}
                />
            </Flex>
        </Layout>
    )
}
